<script setup lang="ts">
import type { ManagerPlanStepProjection } from '@skiphr/contracts'
import { Bot, CalendarClock, FileSignature, FolderKanban, Goal, KeyRound, Sparkles, Workflow } from 'lucide-vue-next'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue'

const props = defineProps<{ steps: readonly ManagerPlanStepProjection[] }>()

const KINDS: Record<string, { label: string; icon: Component }> = {
  connection: { label: 'Connection', icon: KeyRound },
  project: { label: 'Project', icon: FolderKanban },
  goal: { label: 'Goal', icon: Goal },
  operational_contract: { label: 'Contract', icon: FileSignature },
  agent: { label: 'Agent', icon: Bot },
  workflow: { label: 'Workflow', icon: Workflow },
  schedule: { label: 'Schedule', icon: CalendarClock },
  other: { label: 'Step', icon: Sparkles },
}
const kind = (creates: string) => KINDS[creates] ?? KINDS.other!

const NODE_HEIGHT = 76
const ROW_GAP = 34
const COLUMN_GAP = 12
const MIN_NODE_WIDTH = 108
const MAX_NODE_WIDTH = 230

const container = ref<HTMLElement>()
const width = ref(600)
const hovered = ref<number>()
let observer: ResizeObserver | undefined

onMounted(() => {
  if (!container.value) return
  width.value = container.value.clientWidth
  observer = new ResizeObserver(([entry]) => { if (entry) width.value = entry.contentRect.width })
  observer.observe(container.value)
})
onBeforeUnmount(() => observer?.disconnect())

/** For plans saved without links: the kinds of earlier steps each kind builds on. */
const INFERRED_PARENT_KINDS: Record<string, readonly string[]> = {
  goal: ['project'], connection: ['project'], operational_contract: ['project'], agent: ['project'],
  workflow: ['agent'], schedule: ['workflow'],
}

/** 0-based parent indexes per step, from the plan's own links or inferred from step kinds. */
const parents = computed(() => {
  const explicit = props.steps.some((step) => step.dependsOn?.length)
  return props.steps.map((step, index) => {
    if (explicit) return (step.dependsOn ?? []).map((number) => number - 1).filter((parent) => parent >= 0 && parent < index)
    if (index === 0) return []
    const kinds = INFERRED_PARENT_KINDS[step.creates]
    if (!kinds) return step.creates === 'project' ? [] : [index - 1]
    const earlier = props.steps.slice(0, index).map((item, position) => ({ item, position })).filter(({ item }) => kinds.includes(item.creates))
    if (!earlier.length) return [index - 1]
    // A workflow draws on every earlier agent; other kinds attach to the nearest match.
    return step.creates === 'workflow' ? earlier.map(({ position }) => position) : [earlier.at(-1)!.position]
  })
})

/** Drops links already implied through another parent, so the diagram shows only direct hand-offs. */
const directParents = computed(() => {
  const ancestors: Set<number>[] = []
  parents.value.forEach((list, index) => {
    ancestors[index] = new Set(list.flatMap((parent) => [parent, ...ancestors[parent]!]))
  })
  return parents.value.map((list) => list.filter((parent) => !list.some((other) => other !== parent && ancestors[other]!.has(parent))))
})

const layout = computed(() => {
  const levels: number[] = []
  parents.value.forEach((list, index) => { levels[index] = list.length ? Math.max(...list.map((parent) => levels[parent]!)) + 1 : 0 })
  // Each dependency level starts a new row; wide levels wrap onto extra rows.
  const rows: number[][] = []
  for (let level = 0; level <= Math.max(0, ...levels); level += 1) {
    // Order each row under its parents to keep arrows from crossing.
    const placed = new Map(rows.flat().map((index, order) => [index, order]))
    const anchor = (index: number) => {
      const list = directParents.value[index]!.filter((parent) => placed.has(parent))
      return list.length ? list.reduce((sum, parent) => sum + placed.get(parent)!, 0) / list.length : index
    }
    const members = levels.map((value, index) => (value === level ? index : -1)).filter((index) => index >= 0)
      .sort((left, right) => anchor(left) - anchor(right) || left - right)
    // One row per level, never wrapped: wrapping would draw arrows through unrelated steps.
    if (members.length) rows.push(members)
  }
  // Rows too wide for the card scroll sideways instead of shrinking steps below a readable width.
  const widest = Math.max(1, ...rows.map((row) => row.length))
  const canvas = Math.max(width.value, widest * MIN_NODE_WIDTH + (widest - 1) * COLUMN_GAP)
  const nodes = new Map<number, { x: number; y: number; w: number }>()
  rows.forEach((row, rowIndex) => {
    const w = Math.min(MAX_NODE_WIDTH, (canvas - COLUMN_GAP * (row.length - 1)) / row.length)
    const total = w * row.length + COLUMN_GAP * (row.length - 1)
    row.forEach((index, position) => nodes.set(index, {
      x: (canvas - total) / 2 + position * (w + COLUMN_GAP), y: rowIndex * (NODE_HEIGHT + ROW_GAP), w,
    }))
  })
  const edges = directParents.value.flatMap((list, child) => list.map((parent) => {
    const from = nodes.get(parent)!, to = nodes.get(child)!
    const x1 = from.x + from.w / 2, y1 = from.y + NODE_HEIGHT, x2 = to.x + to.w / 2, y2 = to.y - 4
    const bend = Math.max(18, (y2 - y1) / 2)
    return { key: `${parent}-${child}`, parent, child, d: `M ${x1} ${y1} C ${x1} ${y1 + bend}, ${x2} ${y2 - bend}, ${x2} ${y2}` }
  }))
  return { nodes, edges, canvas, height: rows.length * (NODE_HEIGHT + ROW_GAP) - ROW_GAP }
})

// When the diagram is wider than the card, open it centered on the main flow.
watch(() => layout.value.canvas, async (canvas) => {
  await nextTick()
  if (container.value && canvas > width.value) container.value.scrollLeft = (canvas - width.value) / 2
}, { immediate: true })

const related = (index: number) => hovered.value === undefined || hovered.value === index
  || directParents.value[index]!.includes(hovered.value) || directParents.value[hovered.value]!.includes(index)
</script>

<template>
  <div ref="container" class="plan-diagram" role="img" :aria-label="`Plan diagram with ${steps.length} steps`">
  <div class="plan-diagram__canvas" :style="{ width: `${layout.canvas}px`, height: `${layout.height}px` }">
    <svg class="plan-diagram__edges" :width="layout.canvas" :height="layout.height" aria-hidden="true">
      <defs><marker id="plan-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
        <path d="M0 0 L8 4 L0 8 z" fill="currentColor" /></marker></defs>
      <path v-for="edge in layout.edges" :key="edge.key" :d="edge.d" marker-end="url(#plan-arrow)"
        :class="{ 'is-active': hovered !== undefined && (edge.parent === hovered || edge.child === hovered), 'is-dim': hovered !== undefined && edge.parent !== hovered && edge.child !== hovered }" />
    </svg>
    <div v-for="(step, index) in steps" :key="index" class="plan-node" :class="[`plan-node--${KINDS[step.creates] ? step.creates : 'other'}`, { 'is-dim': !related(index) }]"
      :style="{ left: `${layout.nodes.get(index)?.x ?? 0}px`, top: `${layout.nodes.get(index)?.y ?? 0}px`, width: `${layout.nodes.get(index)?.w ?? MIN_NODE_WIDTH}px`, height: `${NODE_HEIGHT}px` }"
      :title="step.detail" tabindex="0" @mouseenter="hovered = index" @mouseleave="hovered = undefined" @focus="hovered = index" @blur="hovered = undefined">
      <span class="plan-node__kind"><component :is="kind(step.creates).icon" :size="12" /> {{ index + 1 }} · {{ kind(step.creates).label }}</span>
      <strong>{{ step.title }}</strong>
    </div>
  </div>
  </div>
</template>
